import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, CheckCircle2, Wallet, ArrowRight, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useRegion } from "@/context/RegionContext";

const MAX_ATTEMPTS = 5;
const POLL_MS = 2000;

export default function MyWalletSuccess({ region = "MY" }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { user, loading: authLoading } = useAuth();
  const { setRegion } = useRegion();
  const sym = region === "MY" ? "RM " : "$";
  const boardPath = region === "MY" ? "/my" : "/";
  const walletPath = region === "MY" ? "/my/wallet" : "/wallet";

  const [status, setStatus] = useState("processing"); // processing | funded | pending
  const [balance, setBalance] = useState(null);
  const [txn, setTxn] = useState(null);
  const startBalance = useRef(null);
  const timer = useRef(null);

  const readBalance = useCallback(async () => {
    const { data } = await api.get("/wallet/balance", { params: { region } });
    return Number(data.balance || 0);
  }, [region]);

  // Fetch the top-up transaction (amount + payment gateway session id) to display.
  const loadTxn = useCallback(async () => {
    try {
      const { data } = await api.get("/wallet", { params: { region } });
      const txns = data.transactions || [];
      const match = sessionId
        ? txns.find((t) => t.tx_type === "topup" && t.reference_id === sessionId)
        : null;
      setTxn(match || txns.find((t) => t.tx_type === "topup") || null);
    } catch {}
  }, [sessionId, region]);

  useEffect(() => {
    document.title = "Top-up — PodiumOne";
    setRegion(region);
    if (authLoading) return;
    if (!user) {
      navigate("/login");
      return;
    }

    let attempts = 0;
    let cancelled = false;

    const run = async () => {
      // Snapshot the balance before the top-up clears.
      try {
        startBalance.current = await readBalance();
        setBalance(startBalance.current);
      } catch {
        startBalance.current = 0;
      }

      const poll = async () => {
        if (cancelled) return;
        attempts += 1;
        try {
          const b = await readBalance();
          setBalance(b);
          if (b > (startBalance.current ?? 0)) {
            setStatus("funded");
            loadTxn();
            return;
          }
        } catch {}
        if (attempts >= MAX_ATTEMPTS) {
          setStatus("pending");
          return;
        }
        timer.current = setTimeout(poll, POLL_MS);
      };
      timer.current = setTimeout(poll, POLL_MS);
    };

    run();
    return () => {
      cancelled = true;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [user, authLoading, navigate, setRegion, readBalance, loadTxn, region]);

  return (
    <div>
      <Navbar />
      <div className="max-w-lg mx-auto px-4 pt-40 pb-24 text-center">
        <AnimatePresence mode="wait">
          {status === "processing" && (
            <motion.div key="proc" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} data-testid="wallet-success-processing">
              <div className="relative w-24 h-24 mx-auto mb-8">
                <div className="absolute inset-0 rounded-full bg-emerald-500/20 blur-2xl animate-pulse" />
                <div className="relative w-24 h-24 rounded-full glass-strong flex items-center justify-center">
                  <Loader2 className="w-10 h-10 text-emerald-400 animate-spin" />
                </div>
              </div>
              <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mb-3">Processing Transfer…</h1>
              <p className="text-white/60 leading-relaxed">
                Confirming your FPX / GrabPay payment. This usually clears in a few seconds — hang tight, your money is safe.
              </p>
              {sessionId && <p className="mt-4 text-xs text-white/30 break-all">Ref: {sessionId}</p>}
            </motion.div>
          )}

          {status === "funded" && (
            <motion.div key="ok" initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} data-testid="wallet-success-funded">
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 260, damping: 18 }} className="w-24 h-24 mx-auto mb-8 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center shadow-[0_0_50px_-10px_rgba(16,185,129,0.8)]">
                <CheckCircle2 className="w-12 h-12 text-emerald-400" />
              </motion.div>
              <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mb-3">Wallet Funded Successfully</h1>
              <p className="text-white/60 mb-6">Your top-up has cleared and your bids are covered.</p>
              <div className="glass-strong rounded-3xl p-6 mb-6">
                <div className="text-sm text-white/50 mb-1">New balance</div>
                <div data-testid="wallet-success-balance" className="font-display font-black text-5xl tracking-tighter text-emerald-400 tabular-nums">
                  {sym}{Number(balance || 0).toFixed(2)}
                </div>
              </div>
              <div data-testid="wallet-success-txn" className="glass rounded-2xl p-5 mb-8 text-left space-y-2.5">
                <div className="text-xs font-bold uppercase tracking-widest text-white/40 mb-1">Transaction details</div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-white/50">Amount topped up</span>
                  <span data-testid="wallet-success-topup-amount" className="font-display font-bold text-emerald-400 tabular-nums">
                    {txn ? `+ ${sym}${Number(txn.amount).toFixed(2)}` : "—"}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm gap-4">
                  <span className="text-white/50 shrink-0">Payment reference</span>
                  <span data-testid="wallet-success-gateway-id" className="font-mono text-xs text-white/60 truncate">
                    {(txn && txn.reference_id) || sessionId || "—"}
                  </span>
                </div>
                {txn?.created_at && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-white/50">Date</span>
                    <span className="text-white/60">{new Date(txn.created_at).toLocaleString()}</span>
                  </div>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button data-testid="wallet-success-return-btn" onClick={() => navigate(boardPath)} className="rounded-full h-12 px-6 bg-gradient-to-r from-emerald-500 to-teal-500 text-black font-semibold active:scale-95 transition-all">
                  Return to Leaderboards <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button data-testid="wallet-success-wallet-btn" variant="outline" onClick={() => navigate(walletPath)} className="rounded-full h-12 px-6 border-white/15 hover:bg-white/10">
                  <Wallet className="w-4 h-4 mr-2" /> View wallet
                </Button>
              </div>
            </motion.div>
          )}

          {status === "pending" && (
            <motion.div key="pend" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} data-testid="wallet-success-pending">
              <div className="w-24 h-24 mx-auto mb-8 rounded-full glass-strong flex items-center justify-center">
                <Clock className="w-11 h-11 text-yellow-400" />
              </div>
              <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mb-3">Almost there…</h1>
              <p className="text-white/60 mb-2 leading-relaxed">
                Your payment is still settling with the bank. FPX transfers can occasionally take a little longer.
              </p>
              <p className="text-white/50 mb-8">
                Current balance: <span className="font-display font-bold text-white">{sym}{Number(balance || 0).toFixed(2)}</span>. It will update automatically once cleared.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button data-testid="wallet-success-refresh-btn" onClick={() => navigate(walletPath)} className="rounded-full h-12 px-6 bg-gradient-to-r from-emerald-500 to-teal-500 text-black font-semibold active:scale-95 transition-all">
                  <Wallet className="w-4 h-4 mr-2" /> Go to wallet
                </Button>
                <Button data-testid="wallet-success-return-btn" variant="outline" onClick={() => navigate(boardPath)} className="rounded-full h-12 px-6 border-white/15 hover:bg-white/10">
                  Return to Board
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
